import axios from "axios";
import store from "@/store/store";
import {postMessageService} from "@/modules/common/services";

const FRONTAGE_API = axios.create({
  baseURL: "/app",
  withCredentials: true
});

FRONTAGE_API.interceptors.request.use(
  config => {
    // Do something before request is sent
    config.headers["x-mobile-app"] = store.getters["isMobileApp"];
    config.headers["x-app-name"] = "Frontage";
    return config;
  }
);

const login = async params => {
  try {
    let result = await FRONTAGE_API.post("login", params);

    if (
      result.data &&
      (result.data.status === "success" || result.data.status === "redirect")
    ) {
      postMessageService.post(params.isPopup ? "login_with_code_callback" : "vue_login_success", result.data);
      return { success: true, error: "" };
    }
  } catch (e) {
    if (!e.response || (e.response && e.response.status === 500)) {
      return { success: false, error: "server_error" };
    }

    // Redirect to login page if the user is not recognized by CloudFlare
    if (e.response && e.response.status === 403) {
      const url = new URL(window.location.href);
      window.location.replace(`${url.origin}/app/login`);
      return { success: false, error: "auth_error" };
    }

    if (e.response && e.response.status === 401) {
      return { success: false, error: "invalid_credentials" };
    }
  }
};

let jwtProcessing = false
let jwtPromise = null;

const getToken = () => {
  if (!jwtProcessing) {
    jwtProcessing = true
    jwtPromise = FRONTAGE_API.post("get_token")
  }

  return jwtPromise
    .then(result => {
      jwtProcessing = false
      if (result.data && result.data.status === "success")
        return { 
          success: true, 
          jwt: result.data.jwt, 
          actorUid: result.data.actor_uid, 
          actorType: result.data.actor_type, 
          staffUid: result.data.staff_uid 
        }
    })
    .catch( e => {
      jwtProcessing = false

      if (!e.response || (e.response && e.response.status === 500)) {
        return { success: false, error: "server_error" };
      }

      if (e.response && e.response.status === 401) {
        return { success: false, error: "invalid_credentials" };
      }
    })
};

const getLoginCode = async params => {
  try {
    let result = await FRONTAGE_API.post("send_login_code", params);
    if (result.status == 200 && result.data.status == "success") {
      return { success: true, data: result.data.data, error: "" };
    } else {
      return {
        success: false,
        data: result.data.data,
        error: "invalid_credentials"
      };
    }
  } catch (e) {
    if (!e.response || (e.response && e.response.status === 500)) {
      return { success: false, error: "server_error" };
    }
  }
};

const sendResetPasswordEmail = async params =>{
  try{
  let result = await FRONTAGE_API.post("send_reset_password_email", params);
    if (result.status === 200 && result.data.status === "success") {
      return { success: true, data: result.data.body.body ,error: "" };
    } else {
      return {
        success: false,
        data: result.data.data,
        error: "invalid_credentials"
      };
    }
  }
  catch(e){
    if (!e.response || (e.response && e.response.status === 500)) {
      return { success: false, error: "server_error" };
    }
  }
}

const resetPassword = async params =>{
  try{
    let result = await FRONTAGE_API.post("reset_password", params);
    if (result.status === 200 && result.data.status === "success") {
      return { success: true, data: result.data.body.body, error: "" };
    } else {
      return {
        success: false,
        data: result.data.data,
        error: "invalid_credentials"
      };
    }
  }
  catch(e){
    if (!e.response || (e.response && e.response.status === 500)) {
      return { success: false, error: "server_error" };
    }
  }
}

const setStaffPassword = async params =>{
  try{
    let result = await FRONTAGE_API.post("set_staff_password", params);
    if (result.status === 200 && result.data.status === "success") {
      return { success: true, data: result.data.body.body, error: "" };
    } else {
      return {
        success: false,
        data: result.data.data,
        error: "invalid_credentials"
      };
    }
  }
  catch(e){
    if (!e.response || (e.response && e.response.status === 500)) {
      return { success: false, error: "server_error" };
    }
  }
}

const checkResetPasswordTokenExistence = async params =>{
  try{
    let result = await FRONTAGE_API.post("check_reset_password_token_existence", params);
    if (result.status === 200 && result.data.status === "success") {
      return { success: true, exists: result.data.body.body.exists, error: "" };
    } else {
      return {
        success: false,
        data: result.data.data,
        error: "invalid_credentials"
      };
    }
  }
  catch(e){
    if (!e.response || (e.response && e.response.status === 500)) {
      return { success: false, error: "server_error" };
    }
  }
}

const verifyStaffToken = async params => {
  try{
    let result = await FRONTAGE_API.post("verify_staff_auth_token", params);
    if (result.status === 200) {
      return { valid: result.data.body.valid };
    } else {
      return { valid: false }
    }
  }
  catch(e){
    if (!e.response || (e.response && e.response.status === 500)) {
      return { success: false, error: "server_error" };
    }
  }
}
const logout = () => {
  postMessageService.post("force_logout");
  return { success: true, data: {}, error: "" };
}

export const authService = {
  login,
  getLoginCode,
  getToken,
  logout,
  sendResetPasswordEmail,
  resetPassword,
  setStaffPassword,
  checkResetPasswordTokenExistence,
  verifyStaffToken
};
